import { Injectable } from '@angular/core';
import {
	EVENT_KEY_MAP,
	EventRegistry,
	CoreParams,
	SESSION_EVENTS,
	ScenarioParams,
	ScenarioBlindTypes,
	BgTypes,
	BACKGROUND_MODAL_EVENTS,
	SBMContext,
	NON_SCENARIO_KEYS,
	AnyKey,
} from '@core/services/gtm/gtm.events2';
import { fromEvent, merge, throttleTime } from 'rxjs';
import { VariantService } from '@core/services/variant/variant.service';

// God hath forsaken this place in disgust of thou compilers screams of anguish and my metal despair as I foolishly attempt compile-time type checking. There shall be no absolution here, only eternal torment and suffering in a display of hubrius which amounts to over engineering. May the compiler gods have mercy on my soul.
type KeyToEvent = typeof EVENT_KEY_MAP;
type EventForKey<K extends keyof KeyToEvent> = EventRegistry[KeyToEvent[K]];
type CoreKeys = keyof CoreParams;
type ScenarioKeys = keyof ScenarioParams;

type CoreForKey<K extends keyof KeyToEvent> = Omit<CoreParams, 'event' | 'event_key'> & {
	event_key: K;
	event: KeyToEvent[K]; // literal event name for that key
};

type KeysNeedingScenario = {
	[K in keyof KeyToEvent]: EventForKey<K> extends ScenarioParams ? K : never;
}[keyof KeyToEvent];

type KeysNotNeedingScenario = Exclude<keyof KeyToEvent, KeysNeedingScenario>;

type NeedsScenario<K extends keyof KeyToEvent> = EventForKey<K> extends ScenarioParams ? true : false;

type PayloadForKey<K extends keyof KeyToEvent> = Omit<EventForKey<K>, CoreKeys | (NeedsScenario<K> extends true ? ScenarioKeys : never)>;

type PayloadForScenarioKey<K extends KeysNeedingScenario> = Omit<EventForKey<K>, CoreKeys | ScenarioKeys>;

type PayloadForNonScenarioKey<K extends KeysNotNeedingScenario> = Omit<EventForKey<K>, CoreKeys>;

type FinalEventNoScenario<K extends KeysNotNeedingScenario> = CoreForKey<K> & PayloadForNonScenarioKey<K>;

type FinalEventWithScenario<K extends KeysNeedingScenario> = CoreForKey<K> & ScenarioParams & PayloadForScenarioKey<K>;

const NON_SCENARIO_KEY_SET = new Set<KeysNotNeedingScenario>(NON_SCENARIO_KEYS);

function keyNeedsScenario<K extends AnyKey>(key: K): key is Extract<K, KeysNeedingScenario> {
	return !NON_SCENARIO_KEY_SET.has(key as any);
}

declare global {
	interface Window {
		dataLayer: Record<string, any>[];
	}
}

const COOKIE_VISITOR_ID = 'zt_vid'; // should maybe put these in config or events file
const LS_VISITOR_ID = 'zt_visitor_id'; // LS = local storage
const LS_PENDING_END = 'zt_pending_session_end';
const LS_USER_SESSION_NUM = 'zt_user_session_num';
const HIATUS_TIMEOUT = 10 * 60 * 1000; // 10 minutes
const QUICK_NAV_S = 5; // 5 second window counts as quick nav

@Injectable({
	providedIn: 'root',
})
export class GtmService {
	private currentSessionId?: string;
	private currentUserSessionNum?: number;
	private readonly visitorId: string;
	private productMode: 'dyb' | 'shop' = 'dyb';

	private lastActivity = Date.now();
	private hiatusStarted?: number;
	private hiatusNum: number = 0;
	private hiatusResumeNum: number = 0;
	private hiatusTimer?: ReturnType<typeof setTimeout>;

	private scenarioId: string;
	private scenarioNum: number = 0;
	private scenarioBlinds: ScenarioBlindTypes;
	scenarioBgType: BgTypes;

	constructor(private variant: VariantService) {
		if (!window.dataLayer) {
			window.dataLayer = [];
		}

		this.flushPendingSessionEnd();

		this.productMode = this.variant.isShop() ? 'shop' : 'dyb';

		// Might consider putting this in app.component init instead as this id could/should be used elsewhere such as growth book
		const storedVid =
			localStorage.getItem(LS_VISITOR_ID) ||
			document.cookie
				.split('; ')
				.find((c) => c.startsWith(COOKIE_VISITOR_ID + '='))
				?.split('=')[1];
		this.visitorId = storedVid ?? crypto.randomUUID();

		localStorage.setItem(LS_VISITOR_ID, this.visitorId); // Refresh / persist visitor id in local storage
		const exp = new Date(Date.now() + 730 * 86400e3).toUTCString(); // 2 year expiry
		document.cookie = `${COOKIE_VISITOR_ID}=${this.visitorId}; expires=${exp}; path=/; SameSite=Lax`; // Refresh / persist visitor id in cookie

		const storedNum = Number(localStorage.getItem(LS_USER_SESSION_NUM));
		this.currentUserSessionNum = Number.isFinite(storedNum) ? storedNum : 0;

		this.bindLifecycleListeners();
		this.bindUserActivityListeners();
		this.resetHiatusTimer();
	}

	private bindLifecycleListeners() {
		window.addEventListener('pagehide', (ev) => {
			// Fires on every unload (navigate, reload, close)
			console.log('end session triggered');
			if (ev.persisted) {
				return; // page is entering BFCache -> keep session open
			}
			this.recordSessionEnd();
		});
	}

	private recordSessionEnd() {
		if (!this.currentSessionId) {
			return; // no session to end
		}

		this.pushEvent(SESSION_EVENTS.session_end, {});

		localStorage.setItem(
			// stash
			LS_PENDING_END,
			JSON.stringify({
				id: this.currentSessionId,
				num: this.currentUserSessionNum,
				productMode: this.productMode,
				timeStamp: Date.now(),
			}),
		);

		this.currentSessionId = undefined;
	}

	private flushPendingSessionEnd() {
		// TODO: as this is called before gtm is initilised will have to check it sends properly once we have the tag set up
		const raw = localStorage.getItem(LS_PENDING_END);
		if (!raw) {
			return;
		}

		const pending = JSON.parse(raw) as { id: string; num: number; productMode: 'dyb' | 'shop'; timeStamp: number };
		const delay = Math.round((Date.now() - pending.timeStamp) / 1000);
		const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;

		let returnType: 'reload' | 'quick_nav' | 'return';

		if (nav.type === 'reload') {
			returnType = 'reload';
		} else if (nav.type === 'back_forward' && delay <= QUICK_NAV_S) {
			returnType = 'quick_nav';
		} else {
			returnType = 'return';
		}

		this.currentSessionId = pending.id; // This function is called in the constructor before startSession can be called so it should be safe to set these here // Slightly hacky but works
		this.currentUserSessionNum = pending.num; // These are the old values from last session
		this.productMode = pending.productMode;

		this.pushEvent(SESSION_EVENTS.session_end_replay, {
			return_delay_s: delay,
			return_type: returnType,
		});

		this.currentSessionId = undefined; // clear session id although it should be reset in startSession anyway // currentUserSessionNum + productMode is reset in the constructor just after this function is called
		localStorage.removeItem(LS_PENDING_END);
	}

	private bindUserActivityListeners() {
		const noisy = merge(
			fromEvent(window, 'mousemove', { passive: true }),
			fromEvent(window, 'scroll', { passive: true }),
			fromEvent(window, 'touchmove', { passive: true }),
		).pipe(throttleTime(1000, undefined, { leading: true, trailing: false }));

		const quiet = merge(
			fromEvent(window, 'mousedown', { passive: true }),
			fromEvent(window, 'touchstart', { passive: true }),
			fromEvent(window, 'keydown'),
		);

		noisy.subscribe(() => this.markActivity());
		quiet.subscribe(() => this.markActivity());

		document.addEventListener('visibilitychange', () => {
			// Treat tab becoming visible as user activity
			if (!document.hidden) {
				this.markActivity(true); // The logic follows that if this was fired then the tab has just changed from hidden to visible hence the true param
			}
		});
	}

	private markActivity(tabHidden: boolean = document.hidden) {
		const currentTime = Date.now();
		const idleFor = currentTime - this.lastActivity;

		if (idleFor >= HIATUS_TIMEOUT && this.hiatusStarted === undefined) {
			// Been 10 mins or more since last activity and not already in hiatus
			this.fireHiatus(tabHidden); // If hiatus didn't fire while tab was hidden and now tab has just become visible (after >10mins) then fire with tabHidden true
		}

		if (this.hiatusStarted !== undefined) {
			// currently in hiatus
			this.hiatusResumeNum += 1;
			this.pushEvent(SESSION_EVENTS.session_resume, {
				hiatus_s: Math.round(idleFor / 1000), // seconds
				session_resume_num: this.hiatusResumeNum,
			});
			this.hiatusStarted = undefined; // clear hiatus
		}

		this.lastActivity = Date.now();
		this.resetHiatusTimer();
	}

	private resetHiatusTimer() {
		clearTimeout(this.hiatusTimer);
		this.hiatusTimer = setTimeout(() => this.fireHiatus(), HIATUS_TIMEOUT);
	}

	private fireHiatus(tabHidden: boolean = document.hidden) {
		if (this.hiatusStarted !== undefined) {
			return; // Already in a hiatus
		}

		this.hiatusStarted = Date.now(); // time hiatus started
		this.hiatusNum += 1; // increment hiatus count
		this.pushEvent(SESSION_EVENTS.session_hiatus, { session_hiatus_num: this.hiatusNum, tab_hidden: tabHidden });
	}

	public startSession(session_id: string, embedded: boolean, app_version?: string) {
		this.currentUserSessionNum! += 1;
		localStorage.setItem(LS_USER_SESSION_NUM, String(this.currentUserSessionNum));
		this.currentSessionId = session_id;

		this.pushEvent(SESSION_EVENTS.session_start, { visitor_id: this.visitorId, embedded, app_version });
	}

	public sessionReturn(session_id: string, session_return_num: number, app_version?: string) {
		this.currentSessionId = session_id;

		this.pushEvent(SESSION_EVENTS.session_return, { visitor_id: this.visitorId, session_return_num, app_version });
	}

	public startScenario(scenarioBlinds: ScenarioBlindTypes, scenarioBgType: BgTypes, context: SBMContext) {
		this.scenarioId = crypto.randomUUID();
		this.scenarioNum += 1;
		this.scenarioBlinds = scenarioBlinds;
		this.scenarioBgType = scenarioBgType;

		this.pushEvent(BACKGROUND_MODAL_EVENTS.scenario_start, { context });
	}

	// public pushEvent<K extends KeysNeedingScenario>(key: K, payload: PayloadForKey<K>): void;
	// public pushEvent<K extends KeysNotNeedingScenario>(key: K, payload: PayloadForKey<K>): void;
	public pushEvent<K extends keyof KeyToEvent>(key: K, payload: PayloadForKey<K>) {
		if (keyNeedsScenario(key)) {
			const k = key as KeysNeedingScenario;

			const core: CoreForKey<typeof k> = {
				event: EVENT_KEY_MAP[key], // lookup event name (e.g. 'crop_interaction')
				event_key: key, // the specific key/interaction that triggered it (e.g. 'cropper_rotate')
				session_id: this.currentSessionId!,
				user_session_num: this.currentUserSessionNum!,
				product_mode: this.productMode,
			};

			const scenario: ScenarioParams = {
				scenario_id: this.scenarioId!,
				scenario_num: this.scenarioNum!,
				scenario_blinds: this.scenarioBlinds!,
				scenario_bg_type: this.scenarioBgType!,
			};

			const p = payload as PayloadForScenarioKey<typeof key>;

			const finalEvent = {
				...core,
				...scenario,
				...p,
			} satisfies FinalEventWithScenario<typeof k>;

			window.dataLayer.push(finalEvent);
			return;
		}

		const k = key as KeysNotNeedingScenario;

		const core: CoreForKey<typeof k> = {
			event: EVENT_KEY_MAP[k], // lookup event name (e.g. 'crop_interaction')
			event_key: k, // the specific key/interaction that triggered it (e.g. 'cropper_rotate')
			session_id: this.currentSessionId!,
			user_session_num: this.currentUserSessionNum!,
			product_mode: this.productMode,
		};

		const p = payload as PayloadForNonScenarioKey<typeof k>;

		const finalEvent = {
			...core,
			...p,
		} satisfies FinalEventNoScenario<typeof k>;

		window.dataLayer.push(finalEvent);
		return;
	}
}
